import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  ArrowLeft, Activity, Users, MessageSquare, AlertTriangle, FileText,
  DollarSign, Trophy, RefreshCw, ShieldAlert, Database, Mail, Rss, CheckCircle2, Clock,
  Crown, Loader2 as Loader,
} from "lucide-react";

export default function AdminHealthPage() {
  const { user, loading: authLoading } = useAuth({ redirectIfUnauthed: true });
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);

  const fetchHealth = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const r = await api.get("/admin/health");
      setData(r.data);
      setLastRefresh(new Date());
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Couldn't load ops snapshot.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user?.is_admin) {
      fetchHealth();
      intervalRef.current = setInterval(() => fetchHealth(true), 30000); // 30s auto-refresh
      return () => clearInterval(intervalRef.current);
    }
  }, [authLoading, user, fetchHealth]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0F0D0C] text-[#A39A8F] flex items-center justify-center font-mono text-xs uppercase tracking-[0.25em]">
        Loading…
      </div>
    );
  }

  if (!user) return null;

  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] grain" data-testid="admin-health-forbidden">
        <Navbar user={user} />
        <div className="mx-auto max-w-3xl px-6 py-32 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-4">403</div>
          <h1 className="font-serif text-4xl mb-4">Admins only.</h1>
          <p className="font-mono text-sm text-[#A39A8F] mb-8">
            Ops dashboards are reserved for AgentReply admins.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            data-testid="back-to-dashboard"
            className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/20 px-6 py-3 hover:border-[#D95B38] hover:text-[#D95B38]"
          >
            ← Back to dashboard
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] grain" data-testid="admin-health-page">
      <Helmet>
        <title>Ops · AgentReply</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Navbar user={user} />

      <div className="mx-auto max-w-7xl px-6 md:px-12 py-12">
        <button
          onClick={() => navigate("/dashboard")}
          data-testid="admin-health-back"
          className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] hover:text-[#D95B38] mb-8 inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-3 h-3" /> Back to dashboard
        </button>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-3 flex items-center gap-2">
              <Activity className="w-3 h-3" /> Admin / Founder ops
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none">
              The whole picture, <span className="italic text-[#D95B38]">in one tab.</span>
            </h1>
            <p className="font-mono text-xs text-[#A39A8F] mt-3">
              Auto-refreshes every 30s · {lastRefresh ? `last refresh ${lastRefresh.toLocaleTimeString()}` : "loading…"}
            </p>
          </div>
          <button
            onClick={() => fetchHealth()}
            disabled={refreshing}
            data-testid="admin-health-refresh"
            className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#F5F2EB]/20 text-[#A39A8F] hover:border-[#D95B38] hover:text-[#D95B38] px-6 py-3 inline-flex items-center gap-2 self-start disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh now"}
          </button>
        </div>

        {loading || !data ? (
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#7A7068] py-24 text-center" data-testid="admin-health-loading">
            Loading metrics…
          </div>
        ) : (
          <>
            {/* System health banner */}
            {!data.system.mongo_ok && (
              <div className="mb-6 border border-red-500/40 bg-red-500/10 p-4 flex items-center gap-3" data-testid="admin-health-mongo-down">
                <ShieldAlert className="w-5 h-5 text-red-400" strokeWidth={1.5} />
                <div className="font-mono text-xs text-red-300">MongoDB ping failed. Investigate immediately.</div>
              </div>
            )}

            {/* Last 24h tiles */}
            <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-4">Last 24 hours</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12">
              <StatTile label="New signups"   value={data.last_24h.new_signups}        icon={Users}        testid="stat-new-signups" />
              <StatTile label="Trial→Paid"    value={data.last_24h.trial_to_paid_conversions} icon={Trophy} testid="stat-conversions" accent />
              <StatTile label="Payments paid" value={data.last_24h.successful_payments} icon={DollarSign}  testid="stat-paid" />
              <StatTile label="Payments failed" value={data.last_24h.failed_payments}  icon={AlertTriangle} testid="stat-failed" warning={data.last_24h.failed_payments > 0} />
              <StatTile label="Public messages" value={data.last_24h.new_public_messages} icon={MessageSquare} testid="stat-msgs" />
              <StatTile label="Rate-limit blocks" value={data.last_24h.rate_limit_blocks} icon={ShieldAlert} testid="stat-rl" warning={data.last_24h.rate_limit_blocks > 50} />
              <StatTile label="New inquiries" value={data.last_24h.new_inquiries}      icon={Mail}         testid="stat-inquiries" />
              <StatTile label="Revenue (30d)" value={`$${data.revenue_30d.gross_usd.toLocaleString()}`} icon={DollarSign} testid="stat-revenue" big accent />
            </div>

            {/* Signup sparkline */}
            <div className="mb-12 bg-[#1A1715] border border-[#F5F2EB]/10 p-6 md:p-8" data-testid="signup-sparkline">
              <div className="flex items-end justify-between mb-4">
                <h3 className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F]">Signups · last 7 days</h3>
                <div className="font-serif text-2xl text-[#F5F2EB]">
                  {data.last_7d_signups.reduce((s, d) => s + d.count, 0)}
                  <span className="font-mono text-[10px] text-[#7A7068] ml-2">total</span>
                </div>
              </div>
              <Sparkline points={data.last_7d_signups} />
            </div>

            {/* Two-col: Top referrers + Latest inquiries */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
              <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-6 md:p-8" data-testid="top-referrers-card">
                <h3 className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-5 flex items-center gap-2">
                  <Trophy className="w-3 h-3 text-[#D95B38]" strokeWidth={2} /> Top referrers · 30d
                </h3>
                {data.top_referrers_30d.length === 0 ? (
                  <div className="font-mono text-xs text-[#7A7068]">No attributed signups yet this month.</div>
                ) : (
                  <ol className="space-y-3">
                    {data.top_referrers_30d.map((r, i) => (
                      <li key={r.user_id} className="flex items-center justify-between gap-4 border-b border-[#F5F2EB]/5 pb-3 last:border-0" data-testid={`referrer-row-${i}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] w-6">#{i + 1}</span>
                          <div className="min-w-0">
                            <div className="font-serif text-sm text-[#F5F2EB] truncate">{r.name || "Anonymous"}</div>
                            <div className="font-mono text-[10px] text-[#7A7068] truncate">{r.email || r.user_id}</div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-serif text-lg text-[#F5F2EB]">{r.converted} <span className="font-mono text-[10px] text-[#7A7068]">paid</span></div>
                          <div className="font-mono text-[10px] text-[#A39A8F]">{r.signups} signups</div>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-6 md:p-8" data-testid="latest-inquiries-card">
                <h3 className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-5 flex items-center gap-2">
                  <Mail className="w-3 h-3 text-[#D95B38]" strokeWidth={2} /> Latest inquiries
                </h3>
                {data.latest_inquiries.length === 0 ? (
                  <div className="font-mono text-xs text-[#7A7068]">No inquiries yet. Promote the contact form.</div>
                ) : (
                  <ul className="space-y-3">
                    {data.latest_inquiries.map((q, i) => (
                      <li key={q.id} className="flex items-start justify-between gap-4 border-b border-[#F5F2EB]/5 pb-3 last:border-0" data-testid={`inquiry-row-${i}`}>
                        <div className="min-w-0">
                          <div className="font-serif text-sm text-[#F5F2EB] truncate">{q.name}</div>
                          <a href={`mailto:${q.email}`} className="font-mono text-[10px] text-[#A39A8F] hover:text-[#D95B38] truncate block">{q.email}</a>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#D95B38] border border-[#D95B38]/40 px-2 py-1">{q.service_type}</span>
                          <div className="font-mono text-[10px] text-[#7A7068] mt-1">{q.created_at ? new Date(q.created_at).toLocaleString() : ""}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Blog publishing health (TrafficForge autopilot) */}
            {data.blog_health && (
              <BlogHealthCard bh={data.blog_health} />
            )}

            {/* Collection counts */}
            <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-6 md:p-8 mb-12" data-testid="collection-counts">
              <h3 className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-5 flex items-center gap-2">
                <Database className="w-3 h-3 text-[#D95B38]" strokeWidth={2} /> Database snapshot
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {Object.entries(data.counts).map(([k, v]) => (
                  <div key={k} className="bg-[#0F0D0C] border border-[#F5F2EB]/5 p-4" data-testid={`count-${k}`}>
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#7A7068] truncate">{k}</div>
                    <div className={`font-serif text-2xl mt-1 ${v < 0 ? "text-red-400" : "text-[#F5F2EB]"}`}>
                      {v < 0 ? "ERR" : v.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Grant plan tool */}
            <GrantPlanCard />

            <div className="font-mono text-[10px] text-[#7A7068] text-center mt-12">
              Generated {new Date(data.generated_at).toLocaleString()}
              {data.system.mongo_ok ? " · Mongo OK" : " · Mongo DOWN"}
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}

function StatTile({ label, value, icon: Icon, testid, accent, warning, big }) {
  return (
    <div
      data-testid={testid}
      className={`p-5 border ${
        warning ? "border-red-500/40 bg-red-500/5"
        : accent ? "border-[#D95B38]/40 bg-[#D95B38]/5"
        : "border-[#F5F2EB]/10 bg-[#1A1715]"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#A39A8F]">{label}</span>
        <Icon className={`w-3.5 h-3.5 ${warning ? "text-red-400" : accent ? "text-[#D95B38]" : "text-[#7A7068]"}`} strokeWidth={1.5} />
      </div>
      <div className={`font-serif ${big ? "text-3xl" : "text-2xl"} ${warning ? "text-red-300" : "text-[#F5F2EB]"}`}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function Sparkline({ points }) {
  if (!points || points.length === 0) return null;
  const max = Math.max(1, ...points.map((p) => p.count));
  return (
    <div className="flex items-end gap-3 h-32" data-testid="sparkline-bars">
      {points.map((p) => {
        const h = Math.max(2, Math.round((p.count / max) * 100));
        return (
          <div key={p.date} className="flex-1 flex flex-col items-center gap-2 group">
            <div className="font-mono text-[10px] text-[#F5F2EB] opacity-0 group-hover:opacity-100 transition-opacity">{p.count}</div>
            <div
              className="w-full bg-[#D95B38] transition-all"
              style={{ height: `${h}%` }}
              title={`${p.date}: ${p.count}`}
              data-testid={`sparkline-bar-${p.date}`}
            />
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#7A7068]">
              {new Date(p.date).toLocaleDateString(undefined, { weekday: "short" })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GrantPlanCard() {
  const [email, setEmail] = React.useState("");
  const [planId, setPlanId] = React.useState("lifetime");
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState("");

  // Voice clone credits sub-form
  const [credEmail, setCredEmail] = React.useState("");
  const [credAmount, setCredAmount] = React.useState(1);
  const [credBusy, setCredBusy] = React.useState(false);
  const [credResult, setCredResult] = React.useState(null);
  const [credError, setCredError] = React.useState("");

  const PLAN_OPTIONS = [
    { id: "monthly", label: "Pro Monthly · $9.99/mo", desc: "Adds 31 days" },
    { id: "yearly", label: "Pro Yearly · $70/yr", desc: "Adds 366 days" },
    { id: "lifetime", label: "Pro Lifetime · $250", desc: "Forever, no expiry" },
    { id: "trial", label: "Reset Trial · 14 days", desc: "Fresh trial window" },
  ];

  const grant = async (e) => {
    e?.preventDefault();
    setError("");
    setResult(null);
    setBusy(true);
    try {
      const r = await api.post("/admin/users/grant-plan", {
        email: email.trim(),
        plan_id: planId,
        reason: reason.trim(),
      });
      setResult(r.data);
      toast.success(`Granted ${planId} to ${email.trim()}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Grant failed.");
    } finally {
      setBusy(false);
    }
  };

  const grantCredits = async (e) => {
    e?.preventDefault();
    setCredError("");
    setCredResult(null);
    setCredBusy(true);
    try {
      const r = await api.post("/admin/users/grant-credits", {
        email: credEmail.trim(),
        credits: parseInt(credAmount, 10) || 1,
        reason: "ops-dashboard",
      });
      setCredResult(r.data);
      toast.success(`+${credAmount} voice clone credit${credAmount > 1 ? "s" : ""} → ${credEmail.trim()}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setCredError(typeof d === "string" ? d : "Grant failed.");
    } finally {
      setCredBusy(false);
    }
  };

  return (
    <div className="bg-[#1A1715] border border-[#D95B38]/30 p-6 md:p-8 mb-12" data-testid="grant-plan-card">
      <h3 className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-5 flex items-center gap-2">
        <Crown className="w-3 h-3" strokeWidth={2} /> Grant a plan
      </h3>
      <p className="font-mono text-xs text-[#A39A8F] mb-6 leading-relaxed max-w-2xl">
        Manually upgrade any user — yourself, friends, family, press, partners. Skips Stripe entirely.
        Stored in <span className="text-[#E0D8CA]">admin_grants</span> for audit.
      </p>

      <form onSubmit={grant} className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="block md:col-span-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] block mb-2">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            data-testid="grant-email"
            className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38]"
          />
        </label>

        <label className="block md:col-span-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] block mb-2">Plan</span>
          <select
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            data-testid="grant-plan-select"
            className="w-full bg-[#0F0D0C] border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38]"
          >
            {PLAN_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id} className="bg-[#0F0D0C]">{opt.label}</option>
            ))}
          </select>
        </label>

        <label className="block md:col-span-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] block mb-2">Reason (optional)</span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="founder / press / friend comp"
            maxLength={200}
            data-testid="grant-reason"
            className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38]"
          />
        </label>

        <div className="md:col-span-3 flex items-center gap-4 flex-wrap">
          <button
            type="submit"
            disabled={busy || !email}
            data-testid="grant-submit"
            className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] hover:bg-[#c14d2e] disabled:opacity-60 px-6 py-3 transition-colors btn-press flex items-center gap-2"
          >
            {busy && <Loader className="w-4 h-4 animate-spin" />}
            {busy ? "Granting…" : "Grant plan →"}
          </button>

          {error && (
            <div className="font-mono text-xs text-red-400" data-testid="grant-error">{error}</div>
          )}

          {result && (
            <div className="font-mono text-xs text-green-400 flex items-center gap-2" data-testid="grant-success">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {result.email} → <span className="text-[#F5F2EB]">{result.subscription?.plan_label || result.subscription?.plan_id}</span>
              {result.subscription?.lifetime && <span className="text-[#D95B38]">· Lifetime</span>}
            </div>
          )}
        </div>
      </form>

      {/* Voice-clone credits sub-form */}
      <div className="mt-8 pt-6 border-t border-[#F5F2EB]/10">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-3 flex items-center gap-2">
          🎙 Voice clone credits
        </div>
        <p className="font-mono text-xs text-[#A39A8F] mb-4 leading-relaxed max-w-2xl">
          Each credit = one voice clone upload. Grant yourself or comp creators / press / influencers.
        </p>
        <form onSubmit={grantCredits} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block md:col-span-1">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] block mb-2">Email</span>
            <input
              type="email"
              required
              value={credEmail}
              onChange={(e) => setCredEmail(e.target.value)}
              placeholder="user@example.com"
              data-testid="grant-credits-email"
              className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38]"
            />
          </label>

          <label className="block md:col-span-1">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] block mb-2">Credits to add</span>
            <input
              type="number"
              min={1}
              max={100}
              required
              value={credAmount}
              onChange={(e) => setCredAmount(e.target.value)}
              data-testid="grant-credits-amount"
              className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38]"
            />
          </label>

          <div className="md:col-span-1 flex items-end">
            <button
              type="submit"
              disabled={credBusy || !credEmail}
              data-testid="grant-credits-submit"
              className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#D95B38] text-[#D95B38] hover:bg-[#D95B38] hover:text-[#F5F2EB] disabled:opacity-60 px-5 py-3 transition-colors btn-press flex items-center gap-2"
            >
              {credBusy && <Loader className="w-3.5 h-3.5 animate-spin" />}
              {credBusy ? "Granting…" : "Add credits →"}
            </button>
          </div>

          {credError && (
            <div className="md:col-span-3 font-mono text-xs text-red-400" data-testid="grant-credits-error">{credError}</div>
          )}
          {credResult && (
            <div className="md:col-span-3 font-mono text-xs text-green-400 flex items-center gap-2" data-testid="grant-credits-success">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {credResult.email} now has <span className="text-[#F5F2EB]">{credResult.voice_clone_credits} voice clone credit{credResult.voice_clone_credits !== 1 ? "s" : ""}</span>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}


function BlogHealthCard({ bh }) {
  const tf = bh.trafficforge || {};
  const statusColor = {
    healthy: { dot: "bg-green-500", text: "text-green-400", label: "Healthy" },
    delayed: { dot: "bg-amber-500", text: "text-amber-400", label: "Delayed" },
    stale: { dot: "bg-red-500", text: "text-red-400", label: "Stale" },
    never_received: { dot: "bg-[#7A7068]", text: "text-[#7A7068]", label: "Never received" },
  }[tf.status] || { dot: "bg-[#7A7068]", text: "text-[#7A7068]", label: "Unknown" };

  const lastReceived = tf.last_received_at ? new Date(tf.last_received_at) : null;
  const fmtTimeAgo = (h) => {
    if (h === null || h === undefined) return "—";
    if (h < 1) return `${Math.round(h * 60)} min ago`;
    if (h < 24) return `${Math.round(h)}h ago`;
    return `${Math.round(h / 24)}d ago`;
  };

  return (
    <div className="mb-12 bg-[#1A1715] border border-[#F5F2EB]/10 p-6 md:p-8" data-testid="blog-health-card">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <h3 className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] flex items-center gap-2">
          <Rss className="w-3 h-3 text-[#D95B38]" strokeWidth={2} />
          Blog publishing health
        </h3>
        <div className="font-mono text-[10px] text-[#7A7068] flex items-center gap-2" data-testid="blog-health-totals">
          <span className="text-[#F5F2EB] font-serif text-base">{bh.posts_24h || 0}</span>
          <span>posts · 24h</span>
          <span className="text-[#F5F2EB]/30">|</span>
          <span className="text-[#F5F2EB] font-serif text-base">{bh.posts_7d || 0}</span>
          <span>posts · 7d</span>
        </div>
      </div>

      {/* TrafficForge status row */}
      <div
        className={`border ${tf.status === "stale" || tf.status === "never_received" ? "border-red-500/30 bg-red-500/5" : tf.status === "delayed" ? "border-amber-500/30 bg-amber-500/5" : "border-[#F5F2EB]/10"} p-4 mb-5 flex items-start gap-3 flex-wrap`}
        data-testid="blog-health-trafficforge-status"
      >
        <span className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] ${statusColor.text}`}>
          <span className={`w-2 h-2 rounded-full ${statusColor.dot} animate-pulse`} />
          TrafficForge · {statusColor.label}
        </span>
        <div className="flex-1 min-w-[200px] font-mono text-xs text-[#E0D8CA]">
          {tf.last_received_at ? (
            <>
              Last article received <span className="text-[#F5F2EB]">{fmtTimeAgo(tf.hours_since_last)}</span>
              <span className="text-[#7A7068]"> ({lastReceived?.toLocaleString()})</span>
              <span className="block text-[10px] text-[#7A7068] mt-0.5">
                Slug: <span className="text-[#A39A8F]">{tf.last_slug}</span> · Status: <span className="text-[#A39A8F]">{tf.last_status}</span>
              </span>
            </>
          ) : (
            <span className="text-[#E0D8CA]">No TrafficForge article has ever reached this database. If Autopilot is on, check the Custom API Destination URL points at <span className="text-[#D95B38] font-mono">/api/blog/publish</span>.</span>
          )}
        </div>
      </div>

      {/* By status + by source */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-[#0F0D0C] border border-[#F5F2EB]/5 p-4" data-testid="blog-health-by-status">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7A7068] mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-3 h-3" /> Status (all-time)
          </div>
          {Object.keys(bh.by_status_total || {}).length === 0 ? (
            <div className="font-mono text-xs text-[#7A7068]">No posts yet.</div>
          ) : (
            <ul className="space-y-1.5">
              {Object.entries(bh.by_status_total).map(([k, v]) => (
                <li key={k} className="flex justify-between font-mono text-xs">
                  <span className={k === "draft" ? "text-amber-400" : "text-[#E0D8CA]"}>{k}</span>
                  <span className="text-[#F5F2EB]">{v}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-[#0F0D0C] border border-[#F5F2EB]/5 p-4" data-testid="blog-health-by-source">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7A7068] mb-3 flex items-center gap-2">
            <Clock className="w-3 h-3" /> By source · 24h
          </div>
          {Object.keys(bh.by_source_24h || {}).length === 0 ? (
            <div className="font-mono text-xs text-[#7A7068]">No posts in last 24h.</div>
          ) : (
            <ul className="space-y-1.5">
              {Object.entries(bh.by_source_24h).map(([k, v]) => (
                <li key={k} className="flex justify-between font-mono text-xs">
                  <span className="text-[#E0D8CA]">{k}</span>
                  <span className="text-[#F5F2EB]">{v}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* 7-day sparkline */}
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7A7068] mb-3">Posts received · last 7 days</div>
        <Sparkline points={bh.last_7d_posts || []} />
      </div>

      {/* Latest posts list */}
      {bh.latest_posts && bh.latest_posts.length > 0 && (
        <div data-testid="blog-health-latest-posts">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7A7068] mb-3">Latest posts</div>
          <ul className="space-y-2">
            {bh.latest_posts.map((p) => (
              <li key={p.slug} className="flex items-center justify-between gap-3 border-b border-[#F5F2EB]/5 pb-2 last:border-0">
                <a
                  href={`/blog/${p.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-xs text-[#E0D8CA] hover:text-[#D95B38] truncate transition-colors min-w-0"
                >
                  {p.title || p.slug}
                </a>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A39A8F] border border-[#F5F2EB]/15 px-2 py-0.5">{p.source || "manual"}</span>
                  <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 ${p.status === "published" ? "text-green-400 border border-green-500/30" : "text-amber-400 border border-amber-500/30"}`}>{p.status}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

